import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { loginSchema, LoginFormData } from '../schemas/validation';
import { AppHeader } from './AppHeader';
import { PasswordField } from './PasswordField';
import { LoadingScreen } from './LoadingScreen';
import './AuthForms.css';

interface LoginFormProps {
  onSwitchToSignup?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignup }) => {
  const { login, loading } = useAuth();
  const [error, setError] = useState<string>('');
  const [inactivityMessage, setInactivityMessage] = useState<string>('');

  // Check for logout reason message
  useEffect(() => {
    const logoutReason = sessionStorage.getItem('logoutReason');
    if (logoutReason === 'inactivity') {
      setInactivityMessage('You have been logged out due to inactivity. Please log in again.');
    } else if (logoutReason === 'deactivated') {
      setInactivityMessage('Your account has been deactivated. Please contact your administrator.');
    }
    if (logoutReason) {
      sessionStorage.removeItem('logoutReason');
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      remember_me: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      console.log('LoginForm: Attempting login with:', data.username);
      await login(data);
      console.log('LoginForm: Login successful (no 2FA)');
    } catch (err: any) {
      console.log('LoginForm: Login error:', err.message);
      if (err.message === '2FA_REQUIRED') {
        console.log('LoginForm: 2FA required, switching to OTP form');
        // Store credentials for OTP form and trigger 2FA state
        return;
      }
      console.log('LoginForm: Setting error message:', err.message);
      setError(err.message || 'Login failed');
    }
  };

  return (
    <div className="auth-page">
      <AppHeader showUserInfo={false} />
      <div className="auth-form-container">
        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <h2>Login</h2>

        {inactivityMessage && (
          <div className="info-message inactivity-message">
            {inactivityMessage}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            {...register('username')}
            className={errors.username ? 'error' : ''}
            placeholder="Enter your username"
            disabled={loading}
          />
          {errors.username && <span className="field-error">{errors.username.message}</span>}
        </div>

        <PasswordField
          id="password"
          label="Password"
          placeholder="Enter your password"
          register={register}
          error={errors.password?.message}
          disabled={loading}
        />

       

        <button 
          type="submit" 
          disabled={loading}
          className="btn btn-primary submit-button"
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>

        
        </form>
      </div>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Authenticating..." />
    </div>
  );
};